/**
 * GamificationWidget
 * 
 * Header widget showing user's points, level, and streak.
 * Clickable to expand with more details.
 */

import React, { useState } from 'react';
import { useGamification } from './GamificationContext';
import { GamificationProgressPanel } from './GamificationProgressPanel';

/**
 * Compact header widget showing level and points
 */
export const GamificationHeaderWidget = ({ onClick }) => {
  const { profile, currentLevel, nextLevelInfo, loading } = useGamification();
  
  if (loading || !profile) {
    return null;
  }
  
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-amber-50 to-yellow-50 hover:from-amber-100 hover:to-yellow-100 border border-amber-200 rounded-full transition-all group"
      title="View your achievements"
    >
      {/* Level Icon */}
      <span className="text-lg group-hover:animate-bounce">
        {currentLevel?.icon || '🌱'}
      </span>
      
      {/* Points */}
      <span className="text-sm font-semibold text-amber-700">
        {profile.totalPoints?.toLocaleString() || 0}
      </span>
      
      {/* Progress bar to next level */}
      {nextLevelInfo?.nextLevel && (
        <div className="hidden sm:flex items-center gap-1">
          <div className="w-16 h-1.5 bg-amber-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full transition-all"
              style={{ width: `${nextLevelInfo.progress}%` }}
            />
          </div>
        </div>
      )}
      
      {/* Streak indicator */}
      {profile.currentStreak > 0 && (
        <span className="hidden sm:flex items-center gap-0.5 text-sm text-orange-600">
          <span>🔥</span>
          <span className="font-medium">{profile.currentStreak}</span>
        </span>
      )}
    </button>
  );
};

/**
 * Expanded card showing detailed stats (popup version)
 */
export const GamificationExpandedCard = ({ onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end pt-16 pr-4 pointer-events-none">
      <div 
        className="w-96 max-h-[calc(100vh-6rem)] pointer-events-auto animate-scale-up"
        onClick={(e) => e.stopPropagation()}
      >
        <GamificationProgressPanel 
          variant="popup" 
          showHeader={true}
          onClose={onClose}
        />
      </div>
      
      {/* Backdrop */}
      <div 
        className="fixed inset-0 -z-10 pointer-events-auto" 
        onClick={onClose}
      />
    </div>
  );
};

/**
 * Combined widget with expand/collapse
 */
export const GamificationWidget = () => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <>
      <GamificationHeaderWidget onClick={() => setExpanded(true)} />
      {expanded && <GamificationExpandedCard onClose={() => setExpanded(false)} />}
    </>
  );
};

export default GamificationWidget;
